import type { Field } from '@/ts/types/schema.types'
import { isNullish } from '@/helpers/type-checks.ts'

export interface ChipTag {
  bgColor: string
  borderColor: string
  iconColor: string
  icon: string
  message: string
}

export function generateField(fieldId: string, value: any): Field {
  const [stepId] = fieldId.split('-')
  return {
    stepId: parseInt(stepId, 10),
    fieldId,
    value
  }
}

export function generateDate(date: string): string {
  const [day, month, year] = date.split('.')
  return `${year}-${month}-${day}`
}

export function generateHumanReadableDate(date: string): string {
  const [year, month, day] = date.split('-')
  return `${day}.${month}.${year}`
}

export function generateChipTag(bgColor: string, borderColor: string, iconColor: string, icon: string, message: string): ChipTag {
  return {
    bgColor,
    borderColor,
    iconColor,
    icon,
    message
  }
}

export function formatPrice(price: number, noCents: boolean = false): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: noCents ? 0 : 2
  })
    .format(price)
    .trim()
}

export function formatPercentage(percentage: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'percent'
  })
    .format(percentage)
    .trim()
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value)
}

export function extractNumberFromFormattedString(value: string): number {
  if (!value) return 0

  const cleanValue = value.replace(/\./g, '').replace(',', '.')
  return parseFloat(cleanValue)
}

export function scrollToTop(): Promise<void> {
  return new Promise((resolve) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })

    function checkScrollEnd() {
      if (window.scrollY === 0) {
        resolve()
      } else {
        window.requestAnimationFrame(checkScrollEnd)
      }
    }

    checkScrollEnd()
  })
}

export async function scrollToElement(query: string, offset: number = 0): Promise<void> {
  const element = document.querySelector(query)
  if (!element) return

  const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0
  const targetScrollPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight - offset

  window.scrollTo({ behavior: 'smooth', top: targetScrollPosition })

  await new Promise<void>((resolve) => {
    requestAnimationFrame(function checkScrollEnd() {
      const currentScrollY = window.scrollY
      const tolerance = 5

      if (Math.abs(currentScrollY - targetScrollPosition) <= tolerance || currentScrollY === 0) {
        resolve()
      } else {
        requestAnimationFrame(checkScrollEnd)
      }
    })
  })
}

export function getDisplayedPrice(
  value: number | null,
  config?: {
    showNull?: boolean
    negate?: boolean
    forcePrefix?: boolean
  }
): string {
  const { showNull, negate, forcePrefix } = { showNull: false, negate: false, forcePrefix: false, ...config }
  if (value === null && !showNull) return ''
  if (isNullish(value)) return formatPrice(0)
  const modifier = negate ? -1 : 1
  const modifiedValue = value !== 0 ? value! * modifier : 0
  const prefix = forcePrefix && modifiedValue > 0 ? '+' : ''
  return `${prefix}${formatPrice(modifiedValue)}`
}
